/**
 * "When should this agent run?" ↔ `spec.triggers` (ADR-0062). The console shows plain choices;
 * the spec keeps the SDK's trigger types (`manual`, `cron`, `webhook`, `email`, `file_drop`,
 * `queue`, `message`). Triggers the form does not edit are kept unchanged.
 */
import { cronToPlan, defaultPlan, planToCron, type SchedulePlan } from "./schedule";
import type { AgentSpec } from "./spec/types";

export type TriggerType = "manual" | "cron" | "webhook" | "email" | "file_drop" | "queue" | "message";

export interface SpecTrigger {
  type: TriggerType | (string & {});
  config?: Record<string, unknown>;
}

/** How the agent starts, as offered in the console. */
export type StartKind = "manual" | "schedule" | "webhook" | "email" | "file_drop" | "queue";
export const START_KINDS: readonly StartKind[] = ["manual", "schedule", "webhook", "email", "file_drop", "queue"];

export interface WhenItRunsModel {
  kind: StartKind;
  /** People can also start it from the Assistant (keeps a `manual` trigger). */
  alsoManual: boolean;
  plan: SchedulePlan;
  timezone: string;
  /** A loaded cron expression the picker cannot show (kept until replaced). */
  customCron: string | null;
  email: { system: string; mailbox: string; folder: string };
  file: { system: string; location: string; pattern: string };
  queue: { system: string; topic: string };
  /** Triggers the form does not edit (e.g. `message`, extra schedules): kept as they are. */
  extra: SpecTrigger[];
  /** A sample of the event that starts the agent (webhook, e-mail, file, queue): its fields
   * become `state.trigger.*` in the agent data map (ADR-0071). */
  sample?: unknown;
}

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);

export function emptyModel(tz: string): WhenItRunsModel {
  return {
    kind: "manual",
    alsoManual: true,
    plan: defaultPlan(tz),
    timezone: tz,
    customCron: null,
    email: { system: "", mailbox: "", folder: "INBOX" },
    file: { system: "", location: "", pattern: "*" },
    queue: { system: "", topic: "" },
    extra: [],
  };
}

export function specTriggers(spec: AgentSpec): SpecTrigger[] {
  const raw = spec.spec.triggers;
  if (!Array.isArray(raw)) return [{ type: "manual" }];
  return raw.filter((t): t is SpecTrigger => !!t && typeof t === "object" && typeof (t as SpecTrigger).type === "string");
}

const KIND_OF: Partial<Record<string, StartKind>> = { cron: "schedule", webhook: "webhook", email: "email", file_drop: "file_drop", queue: "queue" };

/** Reads the first automatic trigger into the form; everything else is kept in `extra`. */
export function modelFromTriggers(triggers: SpecTrigger[], tenantTz: string): WhenItRunsModel {
  const m = emptyModel(tenantTz);
  m.alsoManual = triggers.length === 0 || triggers.some((t) => t.type === "manual");
  let picked = false;
  for (const t of triggers) {
    const c = t.config ?? {};
    const kind = KIND_OF[t.type];
    if (t.type === "manual") continue;
    if (!kind || picked) {
      m.extra.push(t);
      continue;
    }
    picked = true;
    m.kind = kind;
    if (c.sample !== undefined) m.sample = c.sample;
    if (kind === "schedule") {
      const tz = str(c.timezone, tenantTz) || tenantTz;
      m.timezone = tz;
      const cron = str(c.schedule);
      const plan = cronToPlan(cron);
      if (plan) m.plan = { ...defaultPlan(tz), ...plan };
      else m.customCron = cron || null;
    } else if (kind === "email") {
      m.email = { system: str(c.system), mailbox: str(c.mailbox), folder: str(c.folder, "INBOX") };
    } else if (kind === "file_drop") {
      m.file = { system: str(c.system), location: str(c.location), pattern: str(c.pattern, "*") };
    } else if (kind === "queue") {
      m.queue = { system: str(c.system), topic: str(c.topic) };
    }
  }
  if (m.kind === "manual") m.alsoManual = true;
  return m;
}

/** The spec's trigger list for the form. */
export function triggersFromModel(m: WhenItRunsModel): SpecTrigger[] {
  const out: SpecTrigger[] = [];
  if (m.kind === "manual" || m.alsoManual) out.push({ type: "manual" });
  const sample = m.sample !== undefined && m.sample !== null ? { sample: m.sample } : {};
  switch (m.kind) {
    case "schedule":
      out.push({
        type: "cron",
        config: m.customCron
          ? { schedule: m.customCron, timezone: m.timezone }
          : { schedule: planToCron(m.plan), timezone: m.timezone, plan: { ...m.plan } },
      });
      break;
    case "webhook":
      out.push(Object.keys(sample).length ? { type: "webhook", config: { ...sample } } : { type: "webhook" });
      break;
    case "email":
      out.push({ type: "email", config: { ...m.email, ...sample } });
      break;
    case "file_drop":
      out.push({ type: "file_drop", config: { ...m.file, ...sample } });
      break;
    case "queue":
      out.push({ type: "queue", config: { ...m.queue, ...sample } });
      break;
    default:
      break;
  }
  return [...out, ...m.extra];
}

/**
 * Ways of starting that no service listens for yet (planned): offered as "coming soon" and
 * flagged on agents that already use them, because they would never start the agent.
 */
export const UNAVAILABLE_STARTS: ReadonlySet<StartKind> = new Set<StartKind>(["file_drop", "queue"]);

/** Whether the spec's triggers include one that never starts the agent yet. */
export function usesUnavailableStart(triggers: readonly SpecTrigger[]): boolean {
  return triggers.some((t) => UNAVAILABLE_STARTS.has(String((t as { type?: unknown }).type) as StartKind));
}

/** Automatic triggers start runs without a person: the agent must be allowed to work on its own. */
export function needsAutonomy(m: WhenItRunsModel): boolean {
  return m.kind !== "manual";
}

/** Plain-language problems that block saving (keys under `triggers:issue.*`). */
export function triggerIssues(m: WhenItRunsModel): string[] {
  const issues: string[] = [];
  if (m.kind === "email" && !m.email.mailbox.trim()) issues.push("mailbox");
  if (m.kind === "file_drop" && !m.file.location.trim()) issues.push("location");
  if (m.kind === "queue" && !m.queue.topic.trim()) issues.push("topic");
  if (m.kind === "schedule" && !m.customCron) {
    try {
      planToCron(m.plan);
    } catch {
      issues.push("schedule");
    }
  }
  return issues;
}

/** Why the agent cannot be switched to work on its own, or null when it can (SDK rules). */
export function autonomyBlocker(spec: AgentSpec): "nocode" | "tier" | null {
  // A guided (no-code) agent may work on its own only to serve a schedule or event (ADR-0086).
  const automatic = (spec.spec.triggers ?? []).some((t) => (t as { type?: string }).type !== "manual");
  if (spec.spec.builder_profile === "nocode" && !automatic) return "nocode";
  if (Number(spec.spec.risk_tier) >= 3) return "tier";
  return null;
}

/**
 * The blocker for the triggers chosen in the form now, not only the saved ones (G-28): picking a
 * schedule or an event and "work on its own" happen in one version.
 */
export function autonomyBlockerFor(spec: AgentSpec, triggers: SpecTrigger[]): "nocode" | "tier" | null {
  return autonomyBlocker({ ...spec, spec: { ...spec.spec, triggers } });
}

/** Next patch version: drafts are saved under a new version when the latest is submitted. */
export function bumpPatch(version: string): string {
  const m = /^(\d+)\.(\d+)\.(\d+)(?:-[0-9A-Za-z.-]+)?$/.exec(version);
  if (!m) return version;
  return `${m[1]}.${m[2]}.${Number(m[3]) + 1}`;
}

/** The spec with new triggers and autonomy flag (other fields untouched). */
export function withTriggers(spec: AgentSpec, triggers: SpecTrigger[], autonomous: boolean, version?: string): AgentSpec {
  return {
    ...spec,
    metadata: { ...spec.metadata, version: version ?? spec.metadata.version },
    spec: { ...spec.spec, triggers, permissions: { ...(spec.spec.permissions ?? {}), autonomous } },
  };
}
